import React, { useState, useMemo, useEffect, useRef } from 'react';

const ToastContext = React.createContext({
	success: (text, delay, timeout, autohide) => {},
	warning: (text, delay, timeout, autohide) => {},
	error: (text, delay, timeout, autohide) => {},
	doubleToast: (color, text, delay, autoHide, otherColor, otherText, otherDelay, otherAutoHide) => {},
	clear: () => {}
});

export const ToastContextProvider = (props) => {
	const [delay, setDelay] = useState(null);
	const [autohide, setAutoHide] = useState(true);
	const [toastColor, setToastColor] = useState(null);
	const [show, setShow] = useState(false);
	const [toastText, setToastText] = useState(null);

	const [type, setType] = useState(null);
	const [paused, setPaused] = useState(false); // Stato per il timer in pausa
	const toastRef = useRef(null); // Riferimento al toast

	let timeoutId = useRef(null); // Mantieni il riferimento al timeout attivo

	useEffect(() => {
		if (show) {
			toastRef.current?.focus(); // Assegna il focus manualmente quando il toast è visibile
			if (!paused) {
				timeoutId.current = setTimeout(() => {
					setShow(false);
				}, delay);
			}
		}

		return () => clearTimeout(timeoutId.current);
	}, [delay, show, paused]);

	const successHandler = (text, delay = 3000, timeout = 0, autohide = true) => {
		setTimeout(() => {
			showHandler(true, 'green', text, delay, autohide, 'success');
		}, timeout);
	};

	const warningHandler = (text, delay = 3000, timeout = 0, autohide = true) => {
		setTimeout(() => {
			showHandler(true, 'orange', text, delay, autohide, 'warning');
		}, timeout);
	};

	const errorHandler = (text, delay = 3000, timeout = 0, autohide = true) => {
		setTimeout(() => {
			showHandler(true, 'red', text, delay, autohide, 'error');
		}, timeout);
	};

	const clearHandler = () => {
		setShow(false);
	};

	const showHandler = (toShow, color, text, delay, autohide, type) => {
		setToastColor(color);
		setToastText(text);
		setDelay(delay ?? 3000);
		setAutoHide(autohide);
		setShow(toShow);
		setType(type);
	};

	const onCloseToast = () => {
		setShow(false);
	};

	const contextValue = useMemo(() => ({
		success: successHandler,
		warning: warningHandler,
		error: errorHandler,
		clear: clearHandler
	}), []);

	const getToastClass = () => {
		return type === 'success'
			? 'rtds-bg-success-dark'
			: type === 'error'
			? 'rtds-bg-error-dark'
			: 'rtds-bg-warn-dark';
	};

	// Eventi per mettere in pausa e riprendere il timer
	const handleMouseEnter = () => {
		setPaused(true);
		clearTimeout(timeoutId.current); // Ferma il timer
	};

	const handleMouseLeave = () => {
		setPaused(false);
	};

	return (
		<ToastContext.Provider value={contextValue}>
			{props.children}

			{show && (
				<div
					ref={toastRef}
					tabIndex={-1} // Rendi il div focalizzabile
					id='Toast'
					className={`rtds-fixed bottom-5 right-5 max-w-xs rtds-w-full rtds-p-4 rtds-rounded-lg rtds-shadow-lg ${getToastClass()} rtds-text-white rtds-flex rtds-items-center space-x-3`}
					role="alert"
					onClick={onCloseToast}
					onMouseEnter={handleMouseEnter} // Blocca il timer
					onMouseLeave={handleMouseLeave} // Riprendi il timer
				>
					<svg
						className="rtds-icon rtds-fill-current rtds-w-8 rtds-h-8"
						aria-hidden="true"
						focusable="false"
						role="img"
					>
						{type === 'error' && (
							<use xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#outline--exclamation-circle`} />
						)}
						{type === 'success' && (
							<use xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#outline--check-circle`} />
						)}
						{type === '' && (
							<use xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#outline--exclamation-triangle`} />
						)}
					</svg>

					<div className="rtds-flex-1">
						<p>{toastText}</p>
					</div>
					<button onClick={onCloseToast} className="rtds-text-white" aria-label="Chiudi">
						<svg
							className="rtds-icon rtds-fill-current rtds-w-4 rtds-h-4"
							aria-hidden="true"
							focusable="false"
							role="img"
						>
							<use xlinkHref={`${process.env.PUBLIC_URL}/icons.svg#mini--x-mark`} />
						</svg>
					</button>
				</div>
			)}
		</ToastContext.Provider>
	);
};

export default ToastContext;
